/**
 * @file Product page ordering elements (snippets/buy-form.liquid, sections/main-product.liquid,
 * blocks/_product-service.liquid). Import map names: "@theme/utils".
 *
 * <bk-product-form>
 *   Progressive enhancement of the add-to-cart form: without this module the form posts to
 *   /cart/add. With it, `addToCart` posts to the Cart API, re-renders the cart drawer in the same
 *   request and publishes `bk:cart:update`; the cart drawer (cart-drawer.js) opens itself or shows
 *   its toast from that event. A server error (for example a quantity above the stock, HTTP 422)
 *   lands in the form's role="alert" slot. Also runs the quantity stepper (quantity rules come from
 *   the input's min/max/step) and the gift card recipient fields.
 *
 * <bk-product-sticky-bar>
 *   The mobile order bar: shown once the order area (`data-target` id) has scrolled above the
 *   viewport, hidden (and inert) otherwise. Its button scrolls back to the order area and moves
 *   focus there.
 *
 * <bk-delivery-note>
 *   Swaps the delivery row title for "Delivery to {wilaya}" when the visitor chose a wilaya
 *   earlier (localStorage via getWilaya, display only, never fees).
 *
 * Every visible string comes from data-* attributes rendered with `| t`.
 */
import { addToCart, CartError, getWilaya, prefersReducedMotion, subscribe } from '@theme/utils';

const CART_DRAWER_SECTION = 'cart-drawer';

class BkProductForm extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} */
  #request = null;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('submit', (event) => this.#handleSubmit(event), { signal });
    this.addEventListener('click', (event) => this.#handleClick(event), { signal });
    this.addEventListener('change', (event) => this.#handleChange(event), { signal });
    this.addEventListener('input', (event) => this.#handleChange(event), { signal });

    this.#syncStepper();
    this.#syncGiftCard();
    const offset = this.querySelector('[data-gift-card-offset]');
    if (offset instanceof HTMLInputElement) offset.value = String(new Date().getTimezoneOffset());

    this.#unsubscribe = subscribe('bk:variant:change', (detail) => {
      if (!detail?.sectionId || detail.sectionId === this.dataset.sectionId) {
        this.#setError('');
        this.#syncStepper();
      }
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#request?.abort();
    this.#request = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  /** @returns {HTMLFormElement | null} */
  get #form() {
    const form = this.querySelector('form');
    return form instanceof HTMLFormElement ? form : null;
  }

  /** @returns {HTMLButtonElement | null} */
  get #submit() {
    const button = this.querySelector('button[name="add"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /** @returns {HTMLInputElement | null} */
  get #quantity() {
    const input = this.querySelector('[data-quantity] input[name="quantity"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {string} message */
  #setError(message) {
    const slot = this.querySelector('[data-buy-error]');
    if (slot) slot.textContent = message;
  }

  /** @param {string} message */
  #announce(message) {
    const slot = this.querySelector('[data-buy-status]');
    if (!slot || !message) return;
    slot.textContent = '';
    // Re-setting the text on the next frame makes screen readers repeat the same message.
    requestAnimationFrame(() => {
      slot.textContent = message;
    });
  }

  /** @param {SubmitEvent} event */
  async #handleSubmit(event) {
    const form = this.#form;
    if (!form || event.target !== form) return;
    event.preventDefault();

    const button = this.#submit;
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    this.#setError('');
    this.#syncGiftCard();
    button.setAttribute('aria-busy', 'true');
    this.#request?.abort();
    this.#request = new AbortController();

    try {
      await addToCart(new FormData(form), { sections: [CART_DRAWER_SECTION], signal: this.#request.signal });
      this.#announce(this.dataset.addedMessage ?? '');
      // The drawer opens from bk:cart:update; without a cart drawer on the page, go to the cart.
      if (!customElements.get('bk-cart-drawer') || !document.querySelector('bk-cart-drawer')) {
        window.location.assign(window.Shopify?.routes?.root ? `${window.Shopify.routes.root}cart` : '/cart');
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      const description = error instanceof CartError ? error.description : '';
      this.#setError(description || this.dataset.errorMessage || '');
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /** @param {MouseEvent} event */
  #handleClick(event) {
    const target = event.target;
    const step = target instanceof Element ? target.closest('[data-quantity-change]') : null;
    if (!(step instanceof HTMLElement)) return;
    event.preventDefault();
    this.#step(Number(step.dataset.quantityChange) || 0);
  }

  /** @param {Event} event */
  #handleChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name === 'quantity') this.#syncStepper();
    if (target.hasAttribute('data-gift-card-toggle')) this.#syncGiftCard();
  }

  /** @param {number} direction - -1 or 1 */
  #step(direction) {
    const input = this.#quantity;
    if (!input) return;
    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const current = Number(input.value) || min;
    const next = Math.min(max, Math.max(min, current + direction * step));
    if (next === current) return;
    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #syncStepper() {
    const input = this.#quantity;
    if (!input) return;
    const min = Number(input.min) || 1;
    const max = input.max ? Number(input.max) : Number.POSITIVE_INFINITY;
    const current = Number(input.value) || min;
    const minus = this.querySelector('[data-quantity-change="-1"]');
    const plus = this.querySelector('[data-quantity-change="1"]');
    if (minus instanceof HTMLButtonElement) minus.disabled = current <= min;
    if (plus instanceof HTMLButtonElement) plus.disabled = current >= max;
  }

  /** Recipient fields are only sent when the "send to someone" box is ticked. */
  #syncGiftCard() {
    const toggle = this.querySelector('[data-gift-card-toggle]');
    if (!(toggle instanceof HTMLInputElement)) return;
    for (const field of this.querySelectorAll('[data-gift-card-field]')) {
      if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement) field.disabled = !toggle.checked;
    }
  }
}

class BkProductStickyBar extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    this.addEventListener('click', (event) => this.#handleClick(event), { signal: this.#controller.signal });

    const target = this.#target;
    if (!target) return;
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.#setVisible(scrolledPast);
      }
    });
    this.#observer.observe(target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#controller?.abort();
    this.#controller = null;
  }

  /** @returns {HTMLElement | null} The order area this bar stands in for */
  get #target() {
    const id = this.dataset.target;
    const target = id ? document.getElementById(id) : null;
    return target instanceof HTMLElement ? target : null;
  }

  /** @param {boolean} visible */
  #setVisible(visible) {
    this.dataset.visible = String(visible);
    this.toggleAttribute('inert', !visible);
    this.setAttribute('aria-hidden', String(!visible));
  }

  /** @param {MouseEvent} event */
  #handleClick(event) {
    const link = event.target instanceof Element ? event.target.closest('a[data-sticky-order]') : null;
    const target = this.#target;
    if (!link || !target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    target.focus({ preventScroll: true });
  }
}

class BkDeliveryNote extends HTMLElement {
  /** @type {(() => void) | null} */
  #unsubscribe = null;

  connectedCallback() {
    this.#render(getWilaya());
    this.#unsubscribe = subscribe('bk:wilaya:change', (detail) => this.#render(detail?.wilaya ?? ''));
  }

  disconnectedCallback() {
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  /** @param {string} wilaya */
  #render(wilaya) {
    const title = this.querySelector('[data-delivery-title]');
    if (!(title instanceof HTMLElement)) return;
    title.dataset.default ??= title.textContent ?? '';
    const template = this.dataset.template ?? '';
    title.textContent = wilaya && template.includes('[wilaya]') ? template.replace('[wilaya]', wilaya) : title.dataset.default;
  }
}

if (!customElements.get('bk-product-form')) customElements.define('bk-product-form', BkProductForm);
if (!customElements.get('bk-product-sticky-bar')) customElements.define('bk-product-sticky-bar', BkProductStickyBar);
if (!customElements.get('bk-delivery-note')) customElements.define('bk-delivery-note', BkDeliveryNote);
